import { Button } from "@remo-co/ui-core/src/components/Button";
import { useI18n } from "i18n";
import React, { useEffect, useState } from "react";
import { getChargebeeSubscriptionDetail } from "services/apiService/apis";
import useCompany from "modules/company/hooks/useCompany";
import { useCompanyPlanSettings } from "modules/companyPlanSettings";
import { ISubscriptionDetail } from "types/subscription";
import { getVar } from "config";
import logger from "logging/logger";
import PlanChange from "../PlanChange";

interface Props {
  variant?: "text" | "primary" | "secondary" | "ghost";
  color?: "blue" | "gray";
  text?: "string";
  size?: "md" | "sm" | "lg" | "xs";
  className?: string;
}

const ChangePlanButton = ({ variant, color, text, size, className }: Props) => {
  const { t } = useI18n(["settings"]);
  const [isShowPlans, setIsShowPlans] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const { loadCompany } = useCompany();
  const { myCompanyPlan } = useCompanyPlanSettings();
  const [subscription, setSubscriptionDetail] =
    useState<ISubscriptionDetail | null>(null);
  const { Chargebee } = window;
  const cbInstance = Chargebee.init({
    site: getVar("REACT_APP_CHARGEBEE_SITE"),
    enableGATracking: true,
  });
  const cbPortal = cbInstance.createChargebeePortal();

  const refreshSubscriptionInfo = async () => {
    setIsLoading(true);
    const result = await getChargebeeSubscriptionDetail();
    if (result.isSuccess) {
      setSubscriptionDetail({
        subscription: result.subscription,
        plan: result.plan,
        customer: result.customer,
      });
      loadCompany();
    }
    setIsLoading(false);
  };

  const onSubscriptionChange = (data: ISubscriptionDetail) => {
    logger.info("[PlanBilling] subscription changed in portal.", data);

    if (data.subscription && data.subscription.id) {
      refreshSubscriptionInfo();
    }
  };
  const callbacks = {
    subscriptionCancelled: onSubscriptionChange,
    subscriptionReactivated: onSubscriptionChange,
    subscriptionChanged: onSubscriptionChange,
  };

  useEffect(() => {
    if (myCompanyPlan) {
      refreshSubscriptionInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myCompanyPlan]);
  const onClose = async () => {
    setIsLoading(true);
    setIsShowPlans(false);
    // Get latest subscription data
    const result = await getChargebeeSubscriptionDetail();

    // Sync company and guest usage details again
    if (result.isSuccess) {
      loadCompany();
    }
    setIsLoading(false);
  };

  const changePlanOrSubscription = () => {
    if (myCompanyPlan?.chargebeePlanId) {
      cbPortal.open(callbacks, {
        sectionType: Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
        params: {
          subscriptionId: "active_direct",
        },
      });
    } else {
      setIsShowPlans(true);
    }
  };

  return (
    <>
      <Button
        variant={variant || `primary`}
        color={color || `blue`}
        onClick={changePlanOrSubscription}
        disabled={isLoading}
        size={size}
        data-testid="change-plan-button"
        className={`${className}`}
      >
        {text || t("settings:plan.upgrade")}
      </Button>
      <PlanChange
        handleClose={onClose}
        setIsShowPlans={setIsShowPlans}
        isShowPlans={isShowPlans}
        subscriptionDetail={subscription}
        isLoading={isLoading}
      />
    </>
  );
};

export default ChangePlanButton;
